import { DateTime } from "luxon";

export interface DatePeriodValidationError {
  field: string;
  message: string;
}

export interface DiscountValidationErrors {
  activePeriod?: string;
  targetedPeriod?: string;
  general?: string;
}

/**
 * Validates that start date/time is before end date/time
 */
export function validateDatePeriod(
  startDateTime: DateTime,
  endDateTime: DateTime,
  fieldName: string = "period"
): DatePeriodValidationError | null {
  if (!startDateTime || !endDateTime) {
    return {
      field: fieldName,
      message: "Both start and end date/time are required",
    };
  }

  if (!startDateTime.isValid || !endDateTime.isValid) {
    return {
      field: fieldName,
      message: "Invalid date/time format",
    };
  }

  if (startDateTime >= endDateTime) {
    return {
      field: fieldName,
      message: "End date/time must be after start date/time",
    };
  }

  return null;
}

/**
 * Validates that an off-peak hours schedule has at least one day with at least one time slot.
 */
export function validateOffPeakHours(
  offPeakHours: Array<{ dayOfWeek: number; timeSlots: unknown[] }>
): DatePeriodValidationError | null {
  const hasAnySlot = offPeakHours.some((d) => d.timeSlots.length > 0);
  if (!hasAnySlot) {
    return {
      field: "offPeakHours",
      message:
        'At least one day must have a time slot. To remove all conditions, uncheck "Set off-peak hours".',
    };
  }
  return null;
}

/**
 * Validates all discount date periods
 */
export function validateDiscountDatePeriods(
  activeStartDateTime: DateTime,
  activeEndDateTime: DateTime,
  hasTimeInterval: boolean,
  hasOffPeakHours: boolean,
  targetStartDateTime?: DateTime,
  targetEndDateTime?: DateTime,
  offPeakHours?: Array<{ dayOfWeek: number; timeSlots: unknown[] }>
): DiscountValidationErrors {
  const errors: DiscountValidationErrors = {};

  // Validate active period
  const activePeriodError = validateDatePeriod(
    activeStartDateTime,
    activeEndDateTime,
    "activePeriod"
  );
  if (activePeriodError) {
    errors.activePeriod = activePeriodError.message;
  }

  // Validate off-peak hours if enabled
  if (hasOffPeakHours && offPeakHours) {
    const offPeakError = validateOffPeakHours(offPeakHours);
    if (offPeakError) {
      errors.targetedPeriod = offPeakError.message;
    }
  }

  if (hasTimeInterval) {
    if (
      !targetStartDateTime ||
      !targetEndDateTime ||
      !targetStartDateTime.isValid ||
      !targetEndDateTime.isValid
    ) {
      errors.targetedPeriod = "Both start and end date/time are required for targeted period";
    } else {
      // Validate targeted period dates
      const targetPeriodError = validateDatePeriod(
        targetStartDateTime,
        targetEndDateTime,
        "targetedPeriod"
      );
      if (targetPeriodError) {
        errors.targetedPeriod = targetPeriodError.message;
      }
    }
  }

  return errors;
}
